import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  Button,
  Chip,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useState } from "react";

import { useDeleteWasteProfileTest } from "./api";
import {
  EMPTY_VALUE,
  formatProfileDate,
  prettyPrintTestFrequency,
  prettyPrintTestStatus,
  prettyPrintTestType,
  testStatusChipColor,
} from "./profileDisplay";
import { ProfileSection } from "./ProfileSection";
import { PROFILE_TABLE_SX } from "./profileTableSx";
import { WasteProfileSectionData, WasteProfileTestData } from "./types";

const headerSx = { border: 0, py: 0.5 } as const;
const cellSx = { border: 0, py: 0.75 } as const;

const HEADERS = ["Test date", "Type", "Method", "Lab", "Result"] as const;

export const TestingScheduleSection = ({
  profile,
  onAddTest,
  onEditTest,
  onEditSchedule,
  isReadOnly = false,
}: {
  profile: WasteProfileSectionData | null;
  onAddTest: () => void;
  onEditTest: (test: WasteProfileTestData) => void;
  onEditSchedule: () => void;
  isReadOnly?: boolean;
}) => {
  const alerts = useAlerts();
  const [deleteTest, { loading: deleting }] = useDeleteWasteProfileTest();
  const [pendingDelete, setPendingDelete] = useState<WasteProfileTestData | null>(null);

  const tests = profile?.tests ?? [];
  const nextDue = formatProfileDate(profile?.recertifyByDate);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteTest({ variables: { id: pendingDelete.id } });
      alerts.success("Test removed");
    } catch (error) {
      alerts.error("Failed to remove test", error instanceof Error ? error.message : undefined);
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <ProfileSection
      title="Testing schedule"
      adornment={
        <Chip
          size="small"
          variant="outlined"
          color={testStatusChipColor(profile?.testStatus)}
          label={prettyPrintTestStatus(profile?.testStatus)}
        />
      }
      action={
        !isReadOnly ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<ScheduleIcon />}
              onClick={onEditSchedule}
              disabled={!profile}
            >
              Set cadence
            </Button>
            <Button size="small" startIcon={<AddIcon />} onClick={onAddTest} disabled={!profile}>
              Add test
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {prettyPrintTestFrequency(profile?.testFrequency, profile?.testFrequencyEvery)}
          {" · "}
          {nextDue ? `Next due ${nextDue}` : "No next-due date"}
          {profile?.recertifyByDateOverride ? " (manually set)" : ""}
        </Typography>

        {!profile && (
          <Typography variant="body2" color="text.secondary">
            Save a characterization first — the testing schedule hangs off the profile.
          </Typography>
        )}

        {profile && tests.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No tests recorded yet
          </Typography>
        )}

        {profile && tests.length > 0 && (
          <Table size="small" sx={PROFILE_TABLE_SX}>
            <TableHead>
              <TableRow>
                {HEADERS.map((header) => (
                  <TableCell key={header} sx={headerSx}>
                    <Typography variant="caption" color="text.secondary">
                      {header}
                    </Typography>
                  </TableCell>
                ))}
                {!isReadOnly && <TableCell align="right" sx={headerSx} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {tests.map((test) => (
                <TableRow key={test.id}>
                  <TableCell sx={cellSx}>
                    <Typography variant="body2">
                      {formatProfileDate(test.testDate) ?? EMPTY_VALUE}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Typography variant="body2">
                      {prettyPrintTestType(test.type) ?? EMPTY_VALUE}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Typography variant="body2">{test.method || EMPTY_VALUE}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Typography variant="body2">{test.labName || EMPTY_VALUE}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 280 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {test.resultSummary || EMPTY_VALUE}
                      </Typography>
                      {test.resultDocument?.downloadLink && (
                        <Tooltip title={`Download ${test.resultDocument.title}`}>
                          <IconButton
                            size="small"
                            component={Link}
                            href={test.resultDocument.downloadLink}
                            target="_blank"
                            rel="noopener"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  {!isReadOnly && (
                    <TableCell align="right" sx={cellSx}>
                      <IconButton
                        size="small"
                        onClick={() => onEditTest(test)}
                        aria-label="Edit test"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setPendingDelete(test)}
                        aria-label="Remove test"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        header="Remove this test record?"
        info="Removing a test recomputes the next-due date from whatever tests remain."
        resources={[
          {
            resourceType: "Test date",
            resourceName: formatProfileDate(pendingDelete?.testDate) ?? "",
          },
        ]}
        confirmText="Yes, remove test"
        cancelText="Cancel"
        loading={deleting}
      />
    </ProfileSection>
  );
};
